import { Link } from 'react-router-dom';
import Button from '@mui/material/Button';
import SeoHelmet from '../../common/SeoHelmet';
import Header from '../../common/Header';
import Footer from '../../common/Footer';

const CvLLUsNavyTheory = () => {
  return (
    <>
      <SeoHelmet
        title="GeoCalc | Liquid Limit (LL) vs Cv Relationship | US Navy 1982 | Theory"
        description="Empirical correlation between Liquid Limit (LL) and Coefficient of Consolidation (Cv) developed by the U.S. Navy (1982) for quick soil evaluation when lab results are unavailable."
        url="https://www.geocalc.app/cvLLUsNavy-theory"
        image="https://www.geocalc.app/assets/og-theory-v1.jpg"
        schemaType="page"
        breadcrumbs={[
          { name: 'Home', url: 'https://www.geocalc.app/' },
          { name: 'Liquid Limit vs Cv | Theory', url: 'https://www.geocalc.app/cvLLUsNavy-theory' },
        ]}
      />
      <Header />
      <div className="theory">
        <div className="theoryContainer">
          <h1>Liquid Limit (LL) vs Cv Relationship | US Navy 1982 | Theory & Formulations</h1>

          <h2>(U.S. Navy, 1982)</h2>

          <p>
            The <strong>U.S. Navy (1982)</strong> developed an empirical correlation between the{' '}
            <strong>Liquid Limit (LL)</strong> of fine-grained soils and their <strong>Coefficient of Consolidation (Cv)</strong>{' '}
            to estimate Cv quickly when laboratory consolidation test results are unavailable.
          </p>

          <h3>General Concept:</h3>
          <ul>
            <li>
              <strong>Coefficient of Consolidation (Cv)</strong> decreases as the <strong>Liquid Limit (LL)</strong> increases.
            </li>
            <li>
              <strong>Finer-grained, more plastic soils</strong> (higher LL) consolidate <strong>slower</strong> (lower Cv).
            </li>
            <li>
              <strong>Less plastic soils</strong> (lower LL) consolidate <strong>faster</strong> (higher Cv).
            </li>
          </ul>

          <h3>Empirical Relationship:</h3>
          <p>The approximate relationship can be summarized as:</p>
          <p>
            Cv (cm<sup>2</sup>/sec) &#8733; 1 / LL
          </p>
          <p>More specifically:</p>
          <ul>
            <li>
              <strong>High LL (&gt; 50)</strong> &rarr; <strong>Low Cv</strong> (~10<sup>-4</sup> to 10<sup>-5</sup> cm
              <sup>2</sup>/sec)
            </li>
            <li>
              <strong>Moderate LL (30 - 50)</strong> &rarr; <strong>Medium Cv</strong> (~10<sup>-3</sup> to 10<sup>-4</sup> cm
              <sup>2</sup>/sec)
            </li>
            <li>
              <strong>Low LL (&lt; 30)</strong> &rarr; <strong>Higher Cv</strong> (~10<sup>-3</sup> cm<sup>2</sup>/sec)
            </li>
          </ul>

          <h3>Why It Matters:</h3>
          <ul>
            <li>Useful for preliminary foundation designs when lab tests are not ready.</li>
            <li>Especially valuable for military engineering projects needing quick soil evaluations.</li>
            <li>
              However, it&rsquo;s only a rough estimate &mdash; site-specific consolidation tests are still recommended for final
              design.
            </li>
          </ul>

          <h3>Quick Takeaway:</h3>
          <p>
            The <strong>U.S. Navy (1982)</strong> method provides a quick empirical estimate of <strong>Cv</strong> based on{' '}
            <strong>Liquid Limit</strong>, showing that <strong>soils with higher LL</strong> have <strong>lower Cv</strong>{' '}
            (slower consolidation).
          </p>
        </div>
        <div className="theoryButtonContainer">
          <Link to="/calculations/cvLLUsNavy">
            <Button variant="outlined" size="small">
              GO TO CALCULATOR
            </Button>
          </Link>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default CvLLUsNavyTheory;
